import { Button as PrimeButton } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Country } from "../../models/country.model";
import { countryService } from "../../service/country.service";
import PageHeader from "../PageHeader";

const CountryComponent: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [allCountries, setAllCountries] = useState<Country[]>([]);
  const [country, setCountry] = useState<Country>({
    name: "",
    iso2: "",
    iso3: "",
    numericCode: 0,
    phoneCode: 0,
    capital: "",
    timezones: "",
    isActive: true,
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedCountryFilter, setSelectedCountryFilter] =
    useState<Country | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem("countryTablePage");
    return savedPage ? parseInt(savedPage) : 1;
  });
  const [rows, setRows] = useState(() => {
    const savedRows = localStorage.getItem("countryTableRows");
    return savedRows ? parseInt(savedRows) : 10;
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string>(() => {
    const savedSort = localStorage.getItem("countryTableSortField");
    return savedSort || "name";
  });
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0>(() => {
    const savedOrder = localStorage.getItem("countryTableSortOrder");
    return (savedOrder ? parseInt(savedOrder) : 1) as 1 | -1 | 0;
  });
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const loadAllCountries = async () => {
    try {
      const result = await countryService.getAllPaginated({
        pageNumber: 1,
        pageSize: 1000, // Large number to get all countries
        sortField: "name",
        sortOrder: "asc",
      });
      setAllCountries(
        (result?.items ?? []).map((item: any) => ({
          ...item,
          id: typeof item.id === "string" ? parseInt(item.id, 10) : item.id,
        })) || []
      );
    } catch (err) {
      console.error("Error fetching all countries", err);
    }
  };

  useEffect(() => {
    loadCountries();
  }, [page, rows, sortField, sortOrder, globalFilter, selectedCountryFilter]);

  useEffect(() => {
    loadAllCountries(); // Load all countries for the dropdown
  }, []);

  const loadCountries = async () => {
    setLoading(true);
    try {
      const filter = selectedCountryFilter
        ? selectedCountryFilter.name
        : globalFilter;
      const result = await countryService.getAllPaginated({
        pageNumber: page,
        pageSize: rows,
        sortField,
        sortOrder: sortOrder === -1 ? "desc" : "asc",
        globalFilter: filter,
      });
      setCountries(
        (result?.items ?? []).map((item: any) => ({
          ...item,
          id: typeof item.id === "string" ? parseInt(item.id, 10) : item.id,
        })) || []
      );
      setTotalRecords(result?.totalCount ?? 0);
    } catch (err) {
      console.error("Error fetching countries", err);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (country.id) {
        await countryService.update(country.id, country);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Country updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        await countryService.create(country);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Country created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
      await loadCountries();
      setShowForm(false);
    } catch (err: any) {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Failed to save country details.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
    setLoading(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const newValue =
      name === "numericCode" || name === "phoneCode" ? Number(value) : value;
    setCountry({ ...country, [name]: newValue });
  };
  const handleDelete = async (rowData: Country) => {
    if (!rowData.id) return;
  };
  const stateButtonTemplate = (rowData: Country) => (
    <Button
      variant="outline-info"
      onClick={() =>
        navigate(`/states`, {
          state: {
            countryId: rowData.id,
            countryName: rowData.name,
          },
        })
      }
      style={{ padding: "0.35rem 0.75rem" }}
    >
      <Icon.ArrowRight />
    </Button>
  );
  const actionBodyTemplate = (rowData: Country) => (
    <div className="d-flex gap-2">
      <Button
        variant="link"
        className="p-0"
        onClick={() => {
          setCountry(rowData);
          setShowForm(true);
        }}
        title="Edit"
        style={{ color: "#198754" }}
      >
        <Icon.PencilSquare size={20} />
      </Button>
      {/* <Button variant="link" className="p-0" onClick={() => handleDelete(rowData)} title="Delete" style={{ color: '#dc3545' }}>
                <Icon.Trash size={20} />
            </Button> */}
    </div>
  );

  const handleRefresh = () => loadCountries();

  return (
    <div className="content">
      <PageHeader
        title="Country List"
        beforeTitle={
          <PrimeButton
            icon="pi pi-refresh"
            rounded
            text
            severity="secondary"
            onClick={handleRefresh}
            tooltip="Refresh"
            tooltipOptions={{ position: "bottom" }}
            aria-label="Refresh Countries"
          />
        }
      />
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <div className="d-flex align-items-center justify-content-between gap-3 my-3">
                <IconField iconPosition="left" style={{ width: "250px" }}>
                  <InputIcon>
                    <Icon.Search className="tx-12" />
                  </InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                      setSelectedCountryFilter(null);
                      setPage(1);
                      localStorage.setItem("countryTablePage", "1");
                    }}
                    placeholder="Search"
                    style={{ height: "38px" }}
                  />
                </IconField>
                <div className="d-flex align-items-center gap-3">
                  <Dropdown
                    value={selectedCountryFilter}
                    onChange={(e) => {
                      setSelectedCountryFilter(e.value);
                      setPage(1); // Reset to first page when selecting a country
                      setGlobalFilter(""); // Clear the search filter
                    }}
                    options={allCountries}
                    optionLabel="name"
                    placeholder="Select Country"
                    style={{ width: "250px", height: "38px" }}
                    appendTo="self"
                    showClear
                    filter
                    filterBy="name,iso2,iso3"
                  />
                  <Button
                    className="btn btn-primary"
                    style={{ height: "38px", width: "250px" }}
                    onClick={() => {
                      setCountry({
                        name: "",
                        iso2: "",
                        iso3: "",
                        numericCode: 0,
                        phoneCode: 0,
                        capital: "",
                        timezones: "",
                        isActive: true,
                      });
                      setShowForm(true);
                    }}
                  >
                    <Icon.PlusCircle className="me-1" /> Create Country
                  </Button>
                </div>
              </div>

              <DataTable
                value={countries}
                dataKey="id"
                paginator
                first={(page - 1) * rows}
                rows={rows}
                totalRecords={totalRecords}
                onPage={(e) => {
                  const newPage = (e.page ?? 0) + 1;
                  const newRows = e.rows;
                  setPage(newPage);
                  setRows(newRows);
                  localStorage.setItem("countryTablePage", newPage.toString());
                  localStorage.setItem("countryTableRows", newRows.toString());
                }}
                onSort={(e) => {
                  const newSortField = e.sortField || "name";
                  const newSortOrder = e.sortOrder as 1 | -1 | 0;
                  setSortField(newSortField);
                  setSortOrder(newSortOrder);
                  localStorage.setItem("countryTableSortField", newSortField);
                  localStorage.setItem(
                    "countryTableSortOrder",
                    newSortOrder.toString()
                  );
                }}
                sortField={sortField}
                sortOrder={sortOrder}
                lazy
                loading={loading}
                rowsPerPageOptions={[5, 10, 20]}
                stripedRows
                sortMode="single"
                emptyMessage="No countries found."
                className="grid-table"
              >
                <Column field="name" header="Name" sortable />
                <Column field="iso2" header="ISO2" sortable />
                <Column field="iso3" header="ISO3" sortable />
                <Column field="capital" header="Capital" sortable />
                <Column field="phoneCode" header="Phone Code" sortable />
                <Column
                  header="States"
                  body={stateButtonTemplate}
                  style={{ width: "100px", textAlign: "center" }}
                />
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ minWidth: "120px" }}
                />
              </DataTable>

              {showForm && (
                <div
                  className="modal show d-block"
                  tabIndex={-1}
                  style={{ background: "rgba(0,0,0,0.3)" }}
                >
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {country.id ? "Update Country" : "Create Country"}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowForm(false)}
                        ></button>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                          {["name", "iso2", "iso3", "capital", "timezones"].map(
                            (field) => (
                              <div className="mb-3" key={field}>
                                <label className="form-label">
                                  {field.charAt(0).toUpperCase() +
                                    field.slice(1)}
                                  :
                                </label>
                                <input
                                  className="form-control"
                                  name={field}
                                  value={(country as any)[field]}
                                  onChange={handleChange}
                                  required={field !== "capital"}
                                />
                              </div>
                            )
                          )}
                          {["numericCode", "phoneCode"].map((field) => (
                            <div className="mb-3" key={field}>
                              <label className="form-label">{field}:</label>
                              <input
                                type="number"
                                className="form-control"
                                name={field}
                                value={(country as any)[field]}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          ))}
                          {error && (
                            <div className="alert alert-danger">{error}</div>
                          )}
                        </div>
                        <div className="modal-footer">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading
                              ? country.id
                                ? "Updating..."
                                : "Creating..."
                              : country.id
                              ? "Update"
                              : "Create"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowForm(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryComponent;
